/**
 * DatepickerCard — move-in date input in MM/YYYY format.
 *
 * Heyflow widget tag:
 *   <div data-conny-widget="datepicker-card"></div>
 *
 * Writes to sessionStorage:
 *   conny_move_in_date  — "MM/YYYY" once the user has entered a valid date
 */
import { useState } from "react";
import { setBlockValue } from "../../shared/heyflow-bridge";
import { STORAGE_KEYS, useStorage } from "../../shared/storage";

const HEYFLOW_VARIABLE = "conny_movein_date";

export function DatepickerCard() {
  const [stored, setStored] = useStorage(STORAGE_KEYS.moveInDate);
  // Local display value: convert stored YYYY-MM-DD back to MM/YYYY for display.
  const [display, setDisplay] = useState(() => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(stored)) {
      const [yyyy, mm] = stored.split("-");
      return `${mm}/${yyyy}`;
    }
    return stored;
  });
  const [error, setError] = useState("");

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    // Strip everything that isn't a digit (slash is auto-inserted).
    const digits = e.target.value.replace(/\D/g, "").slice(0, 6);

    let formatted = digits.slice(0, 2);
    if (digits.length > 2) formatted += "/" + digits.slice(2);

    setDisplay(formatted);

    if (formatted.length === 7) {
      const month = parseInt(digits.slice(0, 2), 10);
      if (month < 1 || month > 12) {
        setError("Please enter a valid month (01–12).");
      } else {
        setError("");
        // Convert MM/YYYY → YYYY-MM-DD (always first of the month).
        const mm = digits.slice(0, 2);
        const yyyy = digits.slice(2);
        const isoDate = `${yyyy}-${mm}-01`;
        setStored(isoDate);
        setBlockValue(HEYFLOW_VARIABLE, isoDate);
      }
    } else {
      setError("");
      // Clear stored value while the user is still editing.
      if (stored) setStored("");
    }
  }

  return (
    <div className="cn">
      <input
        type="text"
        inputMode="numeric"
        placeholder="MM/YYYY"
        value={display}
        onChange={handleChange}
        maxLength={7}
        className="cn-datepicker-input"
        aria-label="Move-in date (MM/YYYY)"
      />
      {error && <p className="cn-datepicker-error">{error}</p>}
    </div>
  );
}
